/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Feature } from '@app/core/models';
import { Permission } from '@app/core/models/permissions.interface';
import { firstTruthy } from '@app/core/utils/rxjs.utils';
import { FeatureFlagService } from '@app/shared/modules/feature-flag/feature-flag.service';
import { environment } from '@env/environment';
import { Subject, combineLatest, forkJoin } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { PermissionsService } from '../permissions';
import { UserService } from '../user';

// eslint-disable-next-line no-var
declare var pendo: {
  identify: (arg0: {
    visitor: {
      app_name: string;
      email_address: string;
      first_name: string;
      last_name: string;
      home_hotel: string | undefined;
      id: string;
      position: string;
      permissions: Permission[];
      employee_type: 'salaried' | 'hourly' | 'contractor' | '';
      home_hotel_work_week: string;
    };
    account: { id: string };
  }) => void;
  initialize: (arg0: {
    visitor: {
      app_name: string;
      email_address: string;
      first_name: string;
      last_name: string;
      home_hotel: string | undefined;
      id: string;
      position: string;
      permissions: Permission[];
      employee_type: 'salaried' | 'hourly' | 'contractor';
      home_hotel_work_week: string;
    };
    account: { id: string };
  }) => void;
  clearSession: () => void;
};

@Injectable({
  providedIn: 'root',
})
export class PendoService {
  private _identify$ = new Subject<void>();
  private _isInitialized = false;

  constructor(
    private _featureFlagService: FeatureFlagService,
    private _permissionService: PermissionsService,
    private _userService: UserService,
  ) {
    combineLatest([
      this._featureFlagService.isFeatureOn$(Feature.Name.PENDO),
      this._identify$,
    ])
      .pipe(
        filter(([isPendoFeatureFlagOn]) => isPendoFeatureFlagOn),
        switchMap(() =>
          forkJoin([
            this._userService.user$.pipe(firstTruthy()),
            this._permissionService.userPermissions$.pipe(firstTruthy()),
          ]),
        ),
      )
      .subscribe(([user, permissions]) => {
        const homeHotel = user?.allHotels.find(
          (hotel) => hotel.homeHotel === 1,
        );
        const idPrefix = getIdPrefix();
        const visitor = {
          app_name: 'My Hotel Team',
          email_address: user?.email as string,
          first_name: user?.firstName as string,
          last_name: user?.lastName as string,
          home_hotel: homeHotel?.name,
          id: idPrefix + (user?.userName as string),
          position: user?.positionList[0]?.stdposcd as string,
          permissions,
          employee_type: user?.employeeType as
            | 'salaried'
            | 'hourly'
            | 'contractor',
          home_hotel_work_week: user?.workWeek as string,
        };
        const account = {
          id: idPrefix + user?.account.accountName,
        };
        if (this._isInitialized) {
          pendo.identify({ visitor, account });
        } else {
          pendo.initialize({ visitor, account });
          this._isInitialized = true;
        }
      });
  }

  identifyVisitor(): void {
    this._identify$.next();
  }

  clearSession(): void {
    if (this._isInitialized) {
      pendo.clearSession();
    }
  }
}

function getIdPrefix(): string {
  if (environment.production) {
    return '';
  }
  return `my-hotel-team-${environment.envName} `;
}
